import type { ClickToCallSettings, DisplayScheduleDayType } from '../../types';

type TimeSettings = Pick<
  ClickToCallSettings,
  | 'displayScheduleDays'
  | 'displayScheduleTimeEnabled'
  | 'displayScheduleStartTime'
  | 'displayScheduleEndTime'
  | 'displayScheduleTimeZone'
>;

const parseTimeToMinutes = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number);

  return hours * 60 + minutes;
};

const getCurrentTimeParts = (
  timeZone: string
): { day: DisplayScheduleDayType; minutes: number } => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });

  const parts = formatter.formatToParts(new Date());

  const weekday = parts.find(({ type }) => type === 'weekday')?.value;
  const hour = Number(parts.find(({ type }) => type === 'hour')?.value);
  const minute = Number(parts.find(({ type }) => type === 'minute')?.value);

  const dayMap: Record<string, DisplayScheduleDayType> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6
  };

  return {
    day: dayMap[weekday ?? 'Sun'],
    minutes: hour * 60 + minute
  };
};

const isWithinTimeRange = (
  currentMinutes: number,
  startMinutes: number,
  endMinutes: number
) => {
  if (startMinutes === 0 && endMinutes === 0) {
    return true;
  }

  if (startMinutes <= endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }

  return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
};

export const passesTimeRules = ({
  displayScheduleDays,
  displayScheduleTimeEnabled,
  displayScheduleStartTime,
  displayScheduleEndTime,
  displayScheduleTimeZone
}: TimeSettings) => {
  if (!displayScheduleTimeEnabled) {
    return true;
  }

  const timeZone =
    displayScheduleTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { day, minutes } = getCurrentTimeParts(timeZone);

  if (!displayScheduleDays.includes(day)) {
    return false;
  }

  const startMinutes = parseTimeToMinutes(displayScheduleStartTime.time);
  const endMinutes = parseTimeToMinutes(displayScheduleEndTime.time);

  return isWithinTimeRange(minutes, startMinutes, endMinutes);
};
